import TagManager from "react-gtm-module";

export class DataLayer {
  static BusinessInformationOperation({
    flowType = "",
    operation
  }: {
    flowType: string | undefined;
    operation: "created" | "updated";
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "business_information_operation",
        operation: operation,
        flow_type: flowType
      }
    });
  }

  static CompletePackageOrder({
    flowType = "",
    packageType
  }: {
    flowType: string | undefined;
    packageType: string;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "complete_package_order",
        flow_type: flowType,
        package_type: packageType
      }
    });
  }

  static CompleteOrder({ flowType }: { flowType?: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "complete_order",
        flow_type: flowType ?? ""
      }
    });
  }

  static EmailButtonClick({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "email_button_click",
        flow_type: flowType
      }
    });
  }

  static SaveAssets({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_assets",
        flow_type: flowType
      }
    });
  }

  static ChooseShareholderAddress({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "choose_shareholder_address",
        flow_type: flowType
      }
    });
  }

  static CreateCustomAddress({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "create_custom_address",
        flow_type: flowType
      }
    });
  }

  static SaveCompanyAddress({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_company_address",
        flow_type: flowType
      }
    });
  }

  static SavePersonalDetails({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_personal_details",
        flow_type: flowType
      }
    });
  }

  static SaveDirectors({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_directors",
        flow_type: flowType
      }
    });
  }

  static SaveCompanyName({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_company_name",
        flow_type: flowType
      }
    });
  }

  static SaveShares({
    numberOfShares,
    flowType = ""
  }: {
    numberOfShares: number;
    flowType: string | undefined;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "save_shares",
        flow_type: flowType,
        number_of_shares: numberOfShares
      }
    });
  }

  static FormValidationError({
    formName,
    fieldName,
    errorMessage
  }: {
    formName: string;
    fieldName: string;
    errorMessage: string;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "form_validation_error",
        form_name: formName,
        field_name: fieldName,
        error_message: errorMessage
      }
    });
  }

  static StepClosed({
    stepName,
    flowType = ""
  }: {
    stepName: string;
    flowType: string | undefined;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "step_closed",
        flow_type: flowType,
        step_name: stepName
      }
    });
  }

  static StepOpened({
    stepName,
    flowType = ""
  }: {
    stepName: string;
    flowType: string | undefined;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "step_opened",
        flow_type: flowType,
        step_name: stepName
      }
    });
  }

  static CreateShareholder({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "create_shareholder",
        flow_type: flowType
      }
    });
  }

  static DeleteShareholder({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "delete_shareholder",
        flow_type: flowType
      }
    });
  }

  static ExternalDirectorsSubmitted({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "external_directors_submitted",
        flow_type: flowType
      }
    });
  }

  static ShareholderOperation({
    flowType = "",
    numberOfShareholders,
    operation
  }: {
    flowType: string | undefined;
    numberOfShareholders: number;
    operation: "created" | "updated";
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "shareholder_operation",
        operation: operation,
        number_of_shareholders: numberOfShareholders,
        flow_type: flowType
      }
    });
  }

  static StartOrderFlow({ flowType = "" }: { flowType: string | undefined }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "start_order_flow",
        flow_type: flowType
      }
    });
  }

  static Login({ userId, role }: { userId: string; role: string }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "login",
        userId,
        role
      }
    });
  }

  static OrderCompleted({
    userId,
    ordered_products,
    order_summary
  }: {
    userId: string | undefined;
    ordered_products: {
      [product: string]: { product: string; tier?: string; quantity: number; price: number };
    };
    order_summary: {
      one_time_fee?: number;
      discount_value: number;
      total_price: number;
    };
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "order_completed",
        userId,
        ordered_products,
        order_summary
      }
    });
  }

  static AddExistingGmbh({ city }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "add_existing_gmbh",
        city: city ?? ""
      }
    });
  }

  static VirtualPageViewWithinFlow({
    flowType = "",
    stepName
  }: {
    flowType: string | undefined;
    stepName: string;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    this.VirtualPageView({ path: `${flowType}/${stepName}` });
  }

  static VirtualPageView({ path }: { path: string }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "virtual_page_view",
        page_path: path
      }
    });
  }

  static ViewedAdminServiceOffer({ companyCreationId }: { companyCreationId: string }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "viewed_admin_service_offer",
        companyCreationId: companyCreationId ?? ""
      }
    });
  }

  static OptedInAdminServiceOffer({
    companyCreationId,
    adminServiceOrderId
  }: {
    companyCreationId: string;
    adminServiceOrderId: string;
  }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "opted_in_admin_service_offer",
        companyCreationId: companyCreationId,
        adminServiceOrderId: adminServiceOrderId
      }
    });
  }

  static OptedOutAdminServiceOffer({ companyCreationId }: { companyCreationId: string }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "opted_out_admin_service_offer",
        companyCreationId: companyCreationId
      }
    });
  }

  static ConfirmAdminServiceOffer({ adminServiceOrderId }: { adminServiceOrderId: string }) {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "confirm_admin_service_offer",
        adminServiceOrderId: adminServiceOrderId
      }
    });
  }

  static InvestmentOpportunityBanner() {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "investment_opportunity_banner_clicked"
      }
    });
  }

  static LeiRegistrationSubmission() {
    if (this.isGoogleTagManagerDisabled()) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "lei_registration_submitted"
      }
    });
  }

  private static isGoogleTagManagerDisabled = (): boolean =>
    process.env["REACT_APP_GOOGLE_TAG_MANAGER"] !== "true";
}

class GoogleTagManager {
  static DataLayer = DataLayer;
}

export default GoogleTagManager;
